import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  businessEmployeeService,
  clientPackageService,
  IClientPackageView,
  feedback,
} from '../../../shared/services';
import { PAYMENT_METHODS } from '../../../shared/constants/paymentMethods';
import { Button } from '../../../shared/components/MUI/button/Button';

interface IProps {
  open: boolean;
  onClose: () => void;
  clientPackage: IClientPackageView;
  businessId: number;
}

export const RenewPackageModal: React.FC<IProps> = ({
  open,
  onClose,
  clientPackage,
  businessId,
}) => {
  const [paymentMethod, setPaymentMethod] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | undefined>(undefined);
  const queryClient = useQueryClient();

  const { data: employees } = useQuery({
    queryKey: ['businessEmployees', businessId],
    queryFn: () => businessEmployeeService.get(businessId, true),
    enabled: open,
  });

  const { mutate: renew, isLoading } = useMutation(
    () => clientPackageService.renew(clientPackage.id, {
      paymentMethod: paymentMethod || undefined,
      sellerBusinessEmployeeId: selectedEmployeeId,
    }),
    {
      onSuccess: () => {
        feedback('Pacote renovado com sucesso!', 'success');
        queryClient.invalidateQueries({ queryKey: ['clientPackages', businessId] });
        onClose();
      },
      onError: (error: Error) => feedback(error.message, 'error'),
    }
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Renovar Pacote</DialogTitle>
      <DialogContent>
        <Box display='flex' flexDirection='column' gap={2} mt={1}>
          <Typography variant='body1'>
            <strong>Cliente:</strong> {clientPackage.userName}
          </Typography>
          <Typography variant='body1'>
            <strong>Pacote:</strong> {clientPackage.packageName} ({clientPackage.serviceName})
          </Typography>
          <Typography variant='body1'>
            <strong>Valor:</strong> R$ {clientPackage.packageAmount.toFixed(2)}
          </Typography>
          <Typography variant='body1'>
            <strong>Ciclo atual:</strong> {clientPackage.cycle}º
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Ao renovar, o registro anterior será desativado e um novo ciclo será iniciado com
            {clientPackage.totalUses} usos.
          </Typography>

          <FormControl fullWidth size='small'>
            <InputLabel>Profissional (comissão)</InputLabel>
            <Select
              value={selectedEmployeeId ?? ''}
              label='Profissional (comissão)'
              onChange={(e) => setSelectedEmployeeId(e.target.value ? Number(e.target.value) : undefined)}
            >
              <MenuItem value=''>
                <em>Selecionar</em>
              </MenuItem>
              {employees?.map((emp: { id: number; name: string }) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size='small'>
            <InputLabel>Forma de pagamento</InputLabel>
            <Select
              value={paymentMethod}
              label='Forma de pagamento'
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              {PAYMENT_METHODS.map((pm: string) => (
                <MenuItem key={pm} value={pm}>
                  {pm}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant='text'>
          Cancelar
        </Button>
        <Button onClick={() => renew()} isLoading={isLoading} variant='contained'>
          Renovar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
